import { endpoints } from './../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Investment } from './investments-extract/models/investment';
import { map } from 'rxjs/operators/map';
import * as moment from 'moment';
import { FormatPricePipe } from '../../shared/format-price.pipe';
import { Movimentation } from './investments-extract/models/movimentation';
import { Rescue } from './models/rescue';
import { Summary } from './investments-extract/models/summary';
import { PercentPipe } from '@angular/common';
import { Util } from '../../utils/util';
import { Auth } from '../../autentication/Auth';

@Injectable()
export class InvestmentsService {

	investments: Array<Investment>;
	summary: Summary;
	investment: Investment;

	constructor(private http: HttpClient) { }

	getMyInvestments(dateBegin, dateEnd): Observable<Investment[]> {

		const beginDate = moment(dateBegin).format('YYYY-MM-DD');
		const endDate = moment(dateEnd).format('YYYY-MM-DD');

		const params = new HttpParams()
			.set('dataInicial', beginDate)
			.set('dataFinal', endDate);

		return this.http.get( endpoints.investments.all, {params}).pipe( map( (data: any) => {

				if (data.Sucesso) {
					const FP = new FormatPricePipe();

					this.investments = data.Retorno.Lancamentos.map( item => {
						const newInvestment = new Investment();
						newInvestment.number = item.NumeroTitulo;
						newInvestment.code = item.CodPapel;
						newInvestment.description = item.DescPapel;
						newInvestment.applicationDate = moment(item.DataAplicacao).format('DD/MM/YYYY');
						newInvestment.dueDate = moment(item.DataVencimento).format('DD/MM/YYYY');
						newInvestment.rentability = item.Rentabilidade;
						newInvestment.applicationValue = FP.transform(item.ValorAplicado, false);
						newInvestment.currentValue = FP.transform(item.ValorAtual, false);
						if (item.ValorResgate > 0) {
							newInvestment.rescueValue = FP.transform(item.ValorResgate, false);
						}

						const appDate = moment(item.DataAplicacao);
						const dueDate = moment(item.DataVencimento);
						const today = moment();

						if (today.isAfter(dueDate)) {
							newInvestment.percent = 100;
						} else {
							const totalDays = dueDate.diff(appDate, 'days');
							const restDays = dueDate.diff(today, 'days');

							newInvestment.percent = Math.round( 100 - (restDays * 100 / totalDays));
						}

						return newInvestment;
					});
				}

			return this.investments;

		}));

	}

	getSummary(dateBegin, dateEnd): Observable<Summary> {

		const beginDate = moment(dateBegin).format('YYYY-MM-DD');
		const endDate = moment(dateEnd).format('YYYY-MM-DD');

		const params = new HttpParams()
			.set('dataInicial', beginDate)
			.set('dataFinal', endDate);

		return this.http.get(endpoints.investments.all, { params }).pipe(map((data: any) => {
				if (data.Sucesso) {
					const FP = new FormatPricePipe();
					const item = data.Retorno.Resumo;

					this.summary = new Summary();
					this.summary.totalInvestment = FP.transform(item.TotalInvestimento, false);
					this.summary.totalIncome = FP.transform(item.TotalRendimento, false);
					this.summary.grossRescue = FP.transform(item.ResgateBruto, false);
					this.summary.netRescue = FP.transform(item.ResgateLiquido, false);
					this.summary.tax = FP.transform(item.Impostos, false);
					this.summary.totalPre = item.TotalPre;
					this.summary.totalPreValue = FP.transform(item.TotalPre, false);
					this.summary.totalPos = item.TotalPos;
					this.summary.totalPosValue = FP.transform(item.TotalPos, false);
					this.summary.total = item.Total;

					const pp = new PercentPipe('pt-BR');

					this.summary.totalPrePercent = (this.summary.totalPre * 100) / this.summary.total;
					this.summary.totalPrePercentLabel = ` (${pp.transform(this.summary.totalPrePercent/100, '1.1-1')})`;
					this.summary.totalPosPercent = (this.summary.totalPos * 100) / this.summary.total;
					this.summary.totalPosPercentLabel = ` (${pp.transform(this.summary.totalPosPercent/100, '1.1-1')})`;
				}

			return this.summary;

		}));

	}

	getInvestment(code: number): Observable<Investment> {

		return this.http.get( endpoints.investments.all + code ).pipe(map( (data: any) => {

			if (data.Sucesso) {
				const FP = new FormatPricePipe();
				const item = data.Retorno;

				this.investment = new Investment();
				this.investment.number = item.NumeroTitulo;
				this.investment.code = item.CodPapel;
				this.investment.description = item.DescPapel;
				this.investment.applicationDate = moment(item.DataAplicacao).format('DD/MM/YYYY');
				this.investment.dueDate = moment(item.DataVencimento).format('DD/MM/YYYY');
				this.investment.rentability = item.Rentabilidade;
				this.investment.applicationValue = FP.transform(item.ValorInvestido, false);
				this.investment.currentValue = FP.transform(item.ValorAtual, false);
				this.investment.currentBalanceInvestValue = FP.transform(item.ValorSaldoAtualInvestido, false);

				if (item.ValorResgate > 0) {
					this.investment.rescueValue = FP.transform(item.ValorResgate, false);
				}

				const appDate = moment(item.DataAplicacao);
				const dueDate = moment(item.DataVencimento);
				const today = moment();

				if (today.isAfter(dueDate)) {
					this.investment.percent = 100;
				} else {
					const totalDays = dueDate.diff(appDate, 'days');
					const restDays = dueDate.diff(today, 'days');

					this.investment.percent = Math.round( 100 - (restDays * 100 / totalDays));
				}

				this.investment.movimentationList = item.Movimentacoes.map( mov => {
					const newMovimentaion = new Movimentation();
					newMovimentaion.releaseDate = moment(mov.DtLancamento).format('DD/MM/YYYY');
					newMovimentaion.code = mov.CodOperacao;
					newMovimentaion.description = mov.DscOperacao;
					newMovimentaion.mainValue = FP.transform(mov.VlrPrincipal, false);
					newMovimentaion.yeldValue = FP.transform(mov.VlrRendimento, false);
					newMovimentaion.tax = FP.transform(mov.VlrImpostos, false);
					newMovimentaion.liquidValue = FP.transform(mov.VlrLiquido, false);
					newMovimentaion.balanceInvestValue = FP.transform(mov.VlrSldInvestido, false);
					newMovimentaion.isApplication = true;

					if (mov.CodOperacao !== 1) {
						newMovimentaion.isApplication = false;
					}

					return newMovimentaion;
				});

			}

			return this.investment;

		}));

	}

	createHeaders(): HttpHeaders {
		return new HttpHeaders({
			'Assinatura-Eletronica': Auth.assinaturaEletronica ? Auth.assinaturaEletronica : '',
			'Identificador': Auth.getInstance().getGUID(),
			'Omni-Token': Auth.passToken ? Auth.passToken : '',
			'Authorization': 'v2',
		});
	}

	doRescue(titleNumber, value = 0): Observable<Rescue> {	
		let url = endpoints.investments.repurchaseTotal;

		let payload = {
			bondNumber: titleNumber,
			repurchaseValue: value,
			requestId: Util.generateUUID4(),
		}

		if (value) {
			url = endpoints.investments.repurchasePartial;
		}

		const FP = new FormatPricePipe();

		const headers = this.createHeaders()

		return this.http.post( url, payload, { headers }).pipe( map( (data: any) => {
			const resc = new Rescue();
						resc.mainValue = FP.transform(data.result.principalValue, false);
						resc.yeldValue = FP.transform(data.result.yieldValue, false);
						resc.grossRepurchaseAmount = FP.transform(data.result.grossRedemptionValue, false);
						resc.repurchaseAmount = FP.transform(data.result.netRedemptionValue, false);
						resc.tax = FP.transform(data.result.taxValue, false);
						resc.message = data.messages[0];
						resc.success = data.status === 'SUCCESS';

			return resc;

		}));

	}

}



// WEBPACK FOOTER //
// /home/ubuntu/workdir/workspace/Front End Angular (NGINX)/omni-internetbanking/hmg-omni-internetbanking-deploy/src/app/master/investments/investments.service.ts